import React, { useState, useEffect, useCallback } from "react";
import Poster from "../asset/poster.png";

/* ---------------- SAFARI DETECTION ---------------- */

const isSafari = () => {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
};

/* ---------------- STYLES ---------------- */

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  banner: {
    position: "relative",
    width: "98vw",
    maxWidth: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  },
  image: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
    objectFit: "contain",
  },
  closeButton: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "rgba(0,0,0,0.5)",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    width: "20px",
    height: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  closeIcon: {
    width: "20px",
    height: "20px",
    color: "white",
  },
};

/* ---------------- COMPONENT ---------------- */

export default function AppDownloadBanner() {
  const [isVisible, setIsVisible] = useState(false);

  const playstoreLink = "market://details?id=com.kalyanimotors.superapp";
  const iosLink =
    "itms-apps://apps.apple.com/in/app/kalyani-motors-app/id6479185557";
  const deepLinkScheme = "kalyani-motors://";

  const isRealMobileDevice = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (!/iphone|ipad|ipod|android/i.test(ua)) return false;
    if (navigator.maxTouchPoints === 0) return false;

    const sw = window.screen.width;
    const vw = window.innerWidth;
    const sh = window.screen.height;
    const vh = window.innerHeight;

    return !(Math.abs(sw - vw) < 10 && Math.abs(sh - vh) < 50);
  }, []);

  const openAppOrStore = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);

    let appOpened = false;

    const onVisibility = () => {
      if (document.hidden) appOpened = true;
    };
    const onBlur = () => (appOpened = true);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);

    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };

    /* ---------- iOS ---------- */
    if (isIOS) {
      // 🚫 Safari: NEVER auto open deep link
      if (!isSafari()) {
        window.location.href = deepLinkScheme;
      }

      setTimeout(() => {
        if (!appOpened) window.location.href = iosLink;
        cleanup();
      }, 1500);

      return;
    }

    /* ---------- Android ---------- */
    if (isAndroid) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = deepLinkScheme;
      document.body.appendChild(iframe);

      setTimeout(() => {
        document.body.removeChild(iframe);
        if (!appOpened) window.location.href = playstoreLink;
        cleanup();
      }, 1200);
    }
  }, []);

  const handleBannerClick = useCallback(() => {
    const mode = window.innerWidth > 700 ? "desktop" : "mobile";
    localStorage.setItem(`banner-click-${mode}`, Date.now());
    sessionStorage.setItem(`banner-session-${mode}`, "true");
    openAppOrStore();
  }, [openAppOrStore]);

  useEffect(() => {
    const vw = window.innerWidth;
    if (vw > 767) return;
    if (!isRealMobileDevice()) return;

    const mode = vw > 700 ? "desktop" : "mobile";
    const sessionKey = `banner-session-${mode}`;
    const clickKey = `banner-click-${mode}`;

    if (sessionStorage.getItem(sessionKey)) return;

    const lastClick = localStorage.getItem(clickKey);
    const expired = !lastClick || Date.now() - Number(lastClick) > 86400000;

    if (mode === "mobile" && expired && !isSafari()) {
      localStorage.setItem(clickKey, Date.now());
      sessionStorage.setItem(sessionKey, "true");

      setTimeout(() => {
        openAppOrStore();
      }, 100);
    } else {
      setIsVisible(true);
    }
  }, [isRealMobileDevice, openAppOrStore]);

  useEffect(() => {
    document.body.style.overflow = isVisible ? "hidden" : "auto";
    document.documentElement.style.overflow = isVisible ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.banner}>
        <button
          style={styles.closeButton}
          onClick={() => setIsVisible(false)}
          aria-label="Close banner"
        >
          <svg style={styles.closeIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>

        <img
          src={Poster}
          alt="Download Kalyani Motors App"
          style={styles.image}
          onClick={handleBannerClick}
          draggable="false"
        />
      </div>
    </div>
  );
}
